import type { ApexOptions } from 'apexcharts';
import { useEffect, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { type ColumnDefinition, ReactTabulator } from 'react-tabulator';
import type { CellComponent } from 'tabulator-tables';
import type { AggregatedRoadData, ObjectTrafficAll } from '@/shared/types/types-statistic';
import { useColorStore } from '@/store/colorStore';
import { useThemeStore } from '@/store/themeStore';
import { lttbGapAware } from '../utils/lttb';

// Coerce a chart cell to number | null (null = gap, preserved by the line chart).
const numOrNull = (v: number | string | null | undefined): number | null =>
  v === null || v === undefined ? null : Number(v);

const allStatisticTabulator = [
  {
    title: 'No',
    formatter: (cell: CellComponent) => {
      const row = cell.getRow();
      const page = row.getTable().getPage();
      const pageSize = row.getTable().getPageSize();
      return (page - 1) * pageSize + row.getPosition(true);
    },
    width: 60,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '집계시간',
    field: 'aggregated_time',
    hozAlign: 'center',
    widthGrow: 1.2,
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '사이트',
    field: 'site_name',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '접근로',
    field: 'road_name',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '평균 점유율(%)',
    field: 'occupancy_avg',
    width: 120,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '평균속도(km/h)',
    field: 'speed_avg',
    width: 120,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '최고속도(km/h)',
    field: 'speed_max',
    width: 120,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
];

const AllStatistic = ({
  data,
  chartData,
}: {
  data?: AggregatedRoadData[];
  chartData?: ObjectTrafficAll[];
}) => {
  console.log('Data All Table', data);
  console.log('Chart Data All Table', chartData);

  const flatData = data?.flat(Infinity);
  const dataTraffic = flatData?.map((item) => {
    const {
      aggregate_end_time,
      aggregate_start_time,
      aggregated_time,
      occupancy_avg,
      que_len_avg,
      que_len_max,
      road_id,
      road_name,
      site_id,
      site_name,
      speed_avg,
      speed_max,
    } = item;

    return {
      aggregate_end_time,
      aggregate_start_time,
      aggregated_time,
      occupancy_avg,
      que_len_avg,
      que_len_max,
      road_id,
      road_name,
      site_id,
      site_name,
      speed_avg,
      speed_max,
    };
  });
  const chartRecords = chartData as unknown as Record<string, unknown>[] | undefined;
  const dataTrafficChart: Record<string, (number | null | string)[]> | null = chartRecords?.length
    ? (Object.fromEntries(
        Object.keys(chartRecords[0] ?? {}).map((key) => [
          key,
          // chartData.map((item) => item[key] ?? 0),

          // Mapping chartData to make conditional (null ~ no data) and (0 ~ have a data but 0)
          chartRecords.map((item) => {
            const value = item[key];
            return value === null || value === undefined ? null : value;
          }),
        ])
      ) as Record<string, (number | null | string)[]>)
    : null;

  console.log('dataTraffic Chart', dataTrafficChart);

  const { t } = useTranslation();
  const { apexChart: apexColors } = useColorStore();
  // Theme is read here so the tooltip follows the current light/dark mode.
  const isDark = useThemeStore((state) => state.isDark);

  // Downsample to the chart's pixel width (the 3 charts share the grid column width).
  const cellRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  useEffect(() => {
    const el = cellRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setChartWidth(Math.round(w / 50) * 50); // 50px steps to limit re-renders
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const pointBudget = chartWidth > 0 ? Math.max(100, chartWidth) : 1000;

  // Pair each metric value with its bucket time, then gap-aware LTTB per chart.
  const times = (dataTrafficChart?.aggregate_start_time ?? []) as (string | number | null)[];
  const xs = times.map((t) => new Date(t as string).getTime());
  const toXY = (key: string) =>
    lttbGapAware(
      xs.map((x, i) => ({ x, y: numOrNull((dataTrafficChart?.[key] ?? [])[i]) })),
      pointBudget
    );

  const options = {
    chart: {
      type: 'line',
      zoom: {
        enabled: false,
      },
      fontFamily: 'Pretendard GOV',
      toolbar: {
        show: false,
      },
      foreColor: apexColors.foreColor,
    },
    colors: ['#439C50'],
    markers: {
      size: 1,
      colors: ['#EA494E', '#FDCA6A', '#439C50'],
      strokeColors: ['#439C50'],
    },
    stroke: {
      show: true,
      curve: 'straight',
      width: 2,
    },
    grid: {
      borderColor: apexColors.gridBorderColor,
    },
    xaxis: {
      type: 'datetime',
      // Bucket timestamps are UTC ('...Z'); render their wall-clock as-is.
      labels: { rotate: 0, datetimeUTC: true },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      x: { format: 'yyyy-MM-dd HH:mm' },
    },
  };

  const averageOccupancy = [
    {
      name: '평균 점유율(%)',
      data: toXY('occupancy_avg'),
    },
  ] as ApexAxisChartSeries;

  const averageSpeed = [
    {
      name: '평균속도(km/h)',
      data: toXY('speed_avg'),
    },
  ] as ApexAxisChartSeries;

  const maximumSpeed = [
    {
      name: '최고속도(km/h)',
      data: toXY('speed_max'),
    },
  ] as ApexAxisChartSeries;

  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'), //text for the first page button
        first_title: t('cmn > first page'), //tooltip text for the first page button
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const optionsTabulator = {
    pagination: true,
    paginationSize: 10,
    rowHeight: 41,
    movableRows: false,
    index: 'id',
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    resizableRows: false,
    footerElement: `<div style="padding: 0 20px 0 0; text-align: right;">총 ${dataTraffic?.length || 0} 건</div>`,
    placeholder: "<div style='text-align:center;'> No Data</div>",
  };
  return (
    <section>
      <ReactTabulator
        data={dataTraffic}
        columns={allStatisticTabulator as unknown as ColumnDefinition[]}
        layout={'fitColumns'}
        className="tabulator-custom w-full "
        //   pagination="local"
        options={optionsTabulator}
      />

      <section className=" flex! flex-col w-full gap-[20px]">
        <div className="_chartAllStatistic mt-[40px] w-full grid! grid-cols-3 gap-[20px]">
          <div
            ref={cellRef}
            className="border-[3px] rounded-[3px] p-[10px]"
            style={{ borderColor: 'var(--chart-border)' }}
          >
            <span className="title3bold" style={{ color: 'var(--chart-label)' }}>
              평균 점유율
            </span>
            <Chart
              options={options as ApexOptions}
              series={averageOccupancy}
              height={244}
              className={'flex !min-h-[0]'}
              type="line"
            />
          </div>
          <div
            className="border-[3px] rounded-[3px] p-[10px]"
            style={{ borderColor: 'var(--chart-border)' }}
          >
            <span className="title3bold" style={{ color: 'var(--chart-label)' }}>
              평균속도
            </span>
            <Chart
              options={options as ApexOptions}
              series={averageSpeed}
              height={244}
              className={'flex !min-h-[0]'}
              type="line"
            />
          </div>
          <div
            className="border-[3px] rounded-[3px] p-[10px]"
            style={{ borderColor: 'var(--chart-border)' }}
          >
            <span className="title3bold" style={{ color: 'var(--chart-label)' }}>
              최고속도
            </span>
            <Chart
              options={options as ApexOptions}
              series={maximumSpeed}
              height={244}
              className={'flex !min-h-[0]'}
              type="line"
            />
          </div>
        </div>
      </section>
    </section>
  );
};

export default AllStatistic;
